import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ChevronDown, ChevronRight, Check, X, Plus, Scale } from "lucide-react";
import {
    mieterKonto, createAllocation, confirmAllocation, deleteAllocation,
    createCharge, zahlungKeineMiete, ablehnungZuruecknehmen,
} from "@/lib/api";
import { euro, germanDate } from "@/lib/format";
import { useSchreibschutz } from "@/lib/auth";
import { Grund, KEIN_GRUND, grundText } from "@/hilfe/Grund";
import { sperre } from "@/hilfe/Knopfhilfe";
import { Hilfe } from "@/hilfe/Hilfe";
import { toast } from "sonner";

// Der Kontoauszug eines Mietverhältnisses.
//
// WARUM ES IHN GIBT
// Alexander am 04.11.2026: *„Bei Mietern möchte ich sehen, welche Zahlungen
// geflossen sind, um direkt beim Mieter zu erkennen, ob noch etwas säumig
// ist."* Im Mietkonto geht das nur Monat für Monat — und genau die Lage, die
// weh tut, fällt dort nicht auf: Wer jeden Monat 40 € zu wenig überweist,
// hat in keinem einzelnen Monat einen auffälligen Betrag, nach einem Jahr
// aber 480 € Rückstand.
//
// Darum steht die Summe oben und die Monatsliste darunter — nicht umgekehrt.

const AMPEL = {
    rot:   { punkt: "bg-rose-500",    text: "text-rose-700"   },
    gelb:  { punkt: "bg-amber-500",   text: "text-amber-700"  },
    gruen: { punkt: "bg-emerald-500", text: "text-emerald-700" },
};

const Zahl = ({ wert, ton = "neutral", label, fuss }) => (
    <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
        <div className="text-[11px] text-gray-500">{label}</div>
        <div className={`text-lg tabular-nums ${
            ton === "rot" ? "text-rose-600" : ton === "gelb" ? "text-amber-600"
                : ton === "gruen" ? "text-emerald-600" : "text-slate-900"}`}>
            {wert}
        </div>
        {fuss && <div className="text-[10px] text-gray-400 mt-0.5">{fuss}</div>}
    </div>
);

// Wie viele VERSCHIEDENE Überweisungen diesen Monat decken. Nicht die Zahl
// der Zuordnungszeilen: Eine Überweisung, die auf zwei Monate verteilt wird,
// steht in beiden -- sie bleibt aber eine Überweisung.
const ueberweisungen = (p) =>
    new Set((p.zahlungen || []).map((z) => z.payment_id)).size;

const fehlertext = (e) => {
    const d = e?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (d?.error) return [d.error, d.hinweis].filter(Boolean).join(" — ");
    return e?.message || "Unbekannter Fehler";
};

const MieterKontoDialog = ({ tenantId, tenantName, open, onOpenChange }) => {
    const [daten, setDaten] = useState(null);
    const [laedt, setLaedt] = useState(false);
    const [offen, setOffen] = useState(() => new Set());
    const [busy, setBusy] = useState(null);
    // Welche offene Zahlung gerade zugewiesen wird, und wohin.
    const [zuweisen, setZuweisen] = useState(null);     // {payment_id, …}
    const [ziel, setZiel] = useState("");
    const [betrag, setBetrag] = useState("");
    const [neueAbrechnung, setNeueAbrechnung] = useState(null);
    const { gesperrt, grund } = useSchreibschutz();

    const laden = useCallback(() => {
        if (!tenantId) return Promise.resolve();
        setLaedt(true);
        return mieterKonto(tenantId)
            .then(setDaten)
            .catch((e) => toast.error("Kontoauszug nicht geladen",
                                      { description: fehlertext(e) }))
            .finally(() => setLaedt(false));
    }, [tenantId]);

    useEffect(() => {
        if (!open || !tenantId) {
            setDaten(null); setOffen(new Set());
            setZuweisen(null); setNeueAbrechnung(null);
            return;
        }
        laden();
    }, [open, tenantId, laden]);

    // --- Aktionen -----------------------------------------------------------
    const mitLaden = async (schluessel, tun, erfolg) => {
        setBusy(schluessel);
        try { await tun(); toast.success(erfolg); await laden(); }
        catch (e) { toast.error(fehlertext(e)); }
        finally { setBusy(null); }
    };

    const bestaetigen = (id) => mitLaden(
        id, () => confirmAllocation(id), "Bestätigt — zählt jetzt gegen das Soll");

    const loesen = (id) => mitLaden(
        id, () => deleteAllocation(id), "Zuordnung entfernt");

    const keineMiete = (paymentId) => mitLaden(
        paymentId, () => zahlungKeineMiete(paymentId, true),
        'Als „keine Miete“ vermerkt — taucht hier nicht mehr auf');

    const zuweisungSpeichern = () => mitLaden("zuweisen", async () => {
        if (!ziel) throw new Error("Bitte eine Position wählen.");
        await createAllocation({
            payment_id: zuweisen.payment_id, charge_id: ziel,
            amount: betrag === "" ? null : Number(String(betrag).replace(",", ".")),
            status: "bestaetigt", match_reason: "von Hand im Kontoauszug zugewiesen",
        });
        setZuweisen(null); setZiel(""); setBetrag("");
    }, "Zugewiesen und bestätigt");

    const abrechnungSpeichern = () => mitLaden("abrechnung", async () => {
        const a = neueAbrechnung;
        const zahl = Math.abs(parseFloat(String(a.betrag).replace(",", ".")) || 0);
        if (!zahl) throw new Error("Betrag fehlt.");
        if (!a.period) throw new Error("Fälliger Monat fehlt.");
        await createCharge({
            tenant_id: tenantId, art: "nk_abrechnung", period: a.period,
            soll_total: (a.richtung === "guthaben" ? -1 : 1) * zahl,
            abrechnungsjahr: a.abrechnungsjahr || null,
            due_date: a.due_date || null, notes: a.notes || null,
        }, { apply: true });
        setNeueAbrechnung(null);
    }, "Abrechnung angelegt");

    const umschalten = (id) => setOffen((alt) => {
        const n = new Set(alt);
        if (n.has(id)) n.delete(id); else n.add(id);
        return n;
    });

    const s = daten?.summe;
    const ort = daten?.ort;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            {/* Breit und in der Höhe begrenzt: Der Auszug hat sechs Spalten,
                und bei einem Mieter mit zwei Jahren Historie passen die
                Positionen sonst nicht auf den Schirm. overflow-hidden, damit
                nichts seitlich aus dem Dialog läuft -- genau das ist am
                04.11.2026 passiert. */}
            <DialogContent className="max-w-5xl max-h-[88vh] overflow-y-auto overflow-x-hidden"
                           data-testid="mieter-konto">
                <DialogHeader>
                    <DialogTitle>Zahlungen · {tenantName || daten?.mieter?.full_name}</DialogTitle>
                    <DialogDescription>
                        Jede Forderung und was darauf gezahlt wurde. Eine Überweisung kann
                        zwei Monate berühren — dann steht sie bei beiden, mit ihrem Anteil.
                        <Hilfe id="zuordnung" />
                    </DialogDescription>
                </DialogHeader>

                {laedt && <div className="py-10 text-center text-sm text-gray-400">Wird geladen …</div>}

                {daten && !laedt && (
                    <div className="space-y-4">
                        {ort?.immobilie && (
                            <div className="text-xs text-gray-500">
                                {ort.property_id
                                    ? <Link to={`/immobilien/${ort.property_id}`} className="hover:underline">{ort.immobilie}</Link>
                                    : ort.immobilie}
                                {ort.einheit && <>
                                    {" · "}
                                    {ort.unit_id
                                        ? <Link to={`/einheiten/${ort.unit_id}`} className="hover:underline">{ort.einheit}</Link>
                                        : ort.einheit}
                                </>}
                            </div>
                        )}

                        {/* Die Gesamtdifferenz steht vorn: Sie ist die Summe der
                            Monatsdeltas und damit die eine Zahl, die sagt, ob
                            unterm Strich Geld fehlt. */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                            <Zahl label={<>Differenz<Hilfe id="differenz" /></>} wert={euro(s.differenz)}
                                  ton={s.differenz < -0.005 ? "rot"
                                      : s.differenz > 0.005 ? "gelb" : "gruen"}
                                  fuss={s.monate_mit_delta
                                      ? `${s.monate_mit_delta} Monat${s.monate_mit_delta === 1 ? "" : "e"} weichen ab`
                                      : "jeder Monat stimmt"} />
                            <Zahl label={<>Gefordert<Hilfe id="soll-position" /></>} wert={euro(s.soll)}
                                  fuss={`${s.monate} Position${s.monate === 1 ? "" : "en"}`} />
                            <Zahl label={<>Gezahlt<Hilfe id="vorschlag" /></>} wert={euro(s.bezahlt)} ton="gruen"
                                  fuss={s.vorschlag_offen > 0.005
                                      ? `${euro(s.vorschlag_offen)} noch Vorschlag` : "bestätigt"} />
                            <Zahl label={<>Säumig<Hilfe id="saeumig" /></>} wert={euro(s.saeumig)} fuss="fällig und unbezahlt"
                                  ton={s.saeumig > 0.005 ? "rot" : "neutral"} />
                        </div>

                        {s.saeumig <= 0.005 && s.monate_rot === 0 && (
                            <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 text-sm text-emerald-900">
                                Nichts säumig — jede fällige Forderung ist gedeckt.
                            </div>
                        )}

                        <div className="border border-gray-200 rounded-md overflow-hidden">
                            <div className="divide-y divide-gray-100 max-h-[22rem] overflow-y-auto">
                                {daten.positionen.map((p) => {
                                    const auf = offen.has(p.id);
                                    const a = AMPEL[p.ampel] || AMPEL.gelb;
                                    return (
                                        <div key={p.id} data-testid={`konto-position-${p.id}`}>
                                            {/* Festes Raster statt flex: Sechs Spalten mit
                                                fester Breite laufen in einem flex-Container
                                                ueber den Rand hinaus, sobald der Text lang
                                                ist -- genau das ist passiert. Im Raster
                                                bekommt nur die Textspalte den Rest, und
                                                min-w-0 laesst truncate ueberhaupt erst
                                                greifen. */}
                                            <button type="button" onClick={() => umschalten(p.id)}
                                                    className="w-full px-3 py-2 grid items-center gap-2 text-left hover:bg-gray-50
                                                               grid-cols-[14px_8px_62px_minmax(0,1fr)_84px_92px]">
                                                <span className="text-gray-400">
                                                    {auf ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                                                </span>
                                                {/* Der Grund der Ampel kommt aus bewerte()
                                                    und hängt am Punkt (Auftrag 8, Schicht 2).
                                                    Ein Tippen darauf zeigt ihn, ohne die
                                                    Zeile aufzuklappen. */}
                                                <Grund text={p.grund} testId={`konto-ampel-${p.id}`}>
                                                    <span className={`block w-2 h-2 rounded-full ${a.punkt}`} />
                                                </Grund>
                                                <span className="text-sm text-slate-900 tabular-nums">
                                                    {p.period}
                                                </span>
                                                <span className="text-xs text-gray-500 truncate min-w-0">
                                                    {p.art === "miete" ? "Miete" : (
                                                        <>Abrechnung{p.abrechnungsjahr ? ` ${p.abrechnungsjahr}` : ""}</>
                                                    )}
                                                    {" · "}{grundText(p.grund) || KEIN_GRUND}
                                                    {/* „aus 2 Zahlungen" las sich, als haette der
                                                        Mieter zweimal gezahlt. Gemeint ist: Dieser
                                                        Monat wird aus Teilen mehrerer Ueberweisungen
                                                        gedeckt. Gezaehlt werden deshalb die
                                                        verschiedenen Ueberweisungen, nicht die
                                                        Zuordnungszeilen. */}
                                                    {ueberweisungen(p) > 1 && (
                                                        <> · gedeckt aus {ueberweisungen(p)} Überweisungen</>
                                                    )}
                                                </span>
                                                <span className="text-xs text-gray-400 text-right tabular-nums">
                                                    {euro(p.soll)}
                                                </span>
                                                {/* Das Delta dieses Monats, nicht der
                                                    fortgerollte Rest: 0,00 heisst "stimmt",
                                                    −40,00 heisst "hier fehlten 40". */}
                                                <span className={`text-sm text-right tabular-nums ${
                                                    Math.abs(p.delta) <= 0.005 ? "text-emerald-700"
                                                        : p.delta < 0 ? "text-rose-600" : "text-amber-600"}`}>
                                                    {Math.abs(p.delta) <= 0.005 ? "0,00 €"
                                                        : `${p.delta > 0 ? "+" : ""}${euro(p.delta)}`}
                                                </span>
                                            </button>
                                            {auf && (
                                                <div className="px-3 pb-2 pl-[88px] text-xs bg-gray-50/60">
                                                    {p.zahlungen.length === 0 ? (
                                                        <div className="py-1 text-gray-400">
                                                            Keine Zahlung zugeordnet.
                                                        </div>
                                                    ) : p.zahlungen.map((z) => (
                                                        <div key={z.allocation_id}
                                                             className="grid items-center gap-2 py-1 border-t border-gray-100
                                                                        grid-cols-[68px_minmax(0,1fr)_84px_110px_56px]">
                                                            <span className="text-gray-500 tabular-nums">
                                                                {germanDate(z.buchungsdatum)}
                                                            </span>
                                                            <span className="truncate min-w-0 text-slate-700"
                                                                  title={`${z.einzahler || ""} · ${z.zweck || ""}`}>
                                                                {z.einzahler}
                                                                {z.zweck && <span className="text-gray-400"> · {z.zweck}</span>}
                                                            </span>
                                                            <span className="text-right tabular-nums text-slate-900">
                                                                {euro(z.betrag)}
                                                            </span>
                                                            <span className="text-right text-gray-400 truncate min-w-0">
                                                                {/* Anteil einer größeren Überweisung: sonst
                                                                    sieht 403,32 nach einem krummen Betrag aus
                                                                    statt nach einem Teil von 640,00. */}
                                                                {Math.abs(z.zahlung_gesamt - z.betrag) > 0.005 && (
                                                                    <>von {euro(z.zahlung_gesamt)} · </>
                                                                )}
                                                                {z.status === "bestaetigt"
                                                                    ? "bestätigt"
                                                                    : <span className="text-amber-700">Vorschlag</span>}
                                                            </span>
                                                            {/* Änderung und Abweisung gehören an die Zeile:
                                                                Ein Vorschlag, den man nur im Mietkonto
                                                                bestätigen kann, zwingt zum Seitenwechsel
                                                                mitten in der Durchsicht eines Mieters. */}
                                                            <span className="text-right whitespace-nowrap">
                                                                {z.status !== "bestaetigt" && (
                                                                    <Button size="sm" variant="ghost"
                                                                            className="h-6 w-6 p-0 text-emerald-700"
                                                                            disabled={gesperrt || busy === z.allocation_id}
                                                                            grund={sperre([gesperrt, grund], [busy === z.allocation_id, "Wird ausgeführt"])}
                                                                            hilfe="aktion.confirmAllocation"
                                                                            aria-label="Bestätigen"
                                                                            onClick={() => bestaetigen(z.allocation_id)}
                                                                            data-testid={`konto-ok-${z.allocation_id}`}>
                                                                        <Check size={12} />
                                                                    </Button>
                                                                )}
                                                                <Button size="sm" variant="ghost"
                                                                        className="h-6 w-6 p-0 text-gray-400 hover:text-rose-600"
                                                                        disabled={gesperrt || busy === z.allocation_id}
                                                                        grund={sperre([gesperrt, grund], [busy === z.allocation_id, "Wird ausgeführt"])}
                                                                        title="Zuordnung lösen" aria-label="Zuordnung lösen"
                                                                        onClick={() => loesen(z.allocation_id)}
                                                                        data-testid={`konto-weg-${z.allocation_id}`}>
                                                                    <X size={12} />
                                                                </Button>
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {daten.positionen.length === 0 && (
                            <div className="text-sm text-gray-400 text-center py-6">
                                Für dieses Mietverhältnis gibt es noch keine Soll-Positionen.
                            </div>
                        )}

                        {/* ------------------------------------ nicht zugewiesen */}
                        {/* Geld, das auf einem Konto dieser Wohnung liegt und zu
                            keiner Forderung zeigt. Ohne diese Liste sieht ein
                            Mieter säumig aus, dessen Zahlung nur nicht zugeordnet
                            ist — und die Zuordnung müsste man anderswo suchen. */}
                        {daten.offene_zahlungen?.length > 0 && (
                            <div className="border border-amber-200 rounded-md overflow-hidden"
                                 data-testid="konto-offene-zahlungen">
                                <div className="px-4 py-2 bg-amber-50 text-sm font-medium text-slate-900">
                                    Noch nicht zugewiesen · {daten.offene_zahlungen.length}
                                    <Hilfe id="nicht-zugewiesen" />
                                    <span className="font-normal text-xs text-gray-600 ml-2">
                                        Eingänge auf Konten dieser Wohnung, die zu keiner Forderung zeigen
                                    </span>
                                </div>
                                <div className="divide-y divide-gray-100 max-h-56 overflow-y-auto">
                                    {daten.offene_zahlungen.map((z) => (
                                        <div key={z.payment_id} className="px-4 py-2 text-xs"
                                             data-testid={`konto-frei-${z.payment_id}`}>
                                            <div className="grid items-center gap-2
                                                            grid-cols-[68px_minmax(0,1fr)_84px_116px]">
                                                <span className="text-gray-500 tabular-nums">
                                                    {germanDate(z.buchungsdatum)}
                                                </span>
                                                <span className="truncate min-w-0 text-slate-700"
                                                      title={`${z.einzahler || ""} · ${z.zweck || ""}`}>
                                                    {z.einzahler}
                                                    {z.zweck && <span className="text-gray-400"> · {z.zweck}</span>}
                                                </span>
                                                <span className="tabular-nums text-slate-900 text-right">
                                                    {euro(z.rest)}
                                                </span>
                                                <span className="text-right whitespace-nowrap">
                                                    <Button size="sm" variant="outline" className="h-6 px-2"
                                                            disabled={gesperrt || busy === z.payment_id}
                                                            grund={sperre([gesperrt, grund], [busy === z.payment_id, "Wird ausgeführt"])}
                                                            hilfe="aktion.createAllocation"
                                                            onClick={() => {
                                                                setZuweisen(z); setZiel("");
                                                                setBetrag(String(Math.abs(z.rest).toFixed(2)));
                                                            }}
                                                            data-testid={`konto-zuweisen-${z.payment_id}`}>
                                                        zuweisen
                                                    </Button>
                                                    <Button size="sm" variant="ghost"
                                                            className="h-6 px-1.5 text-gray-400 hover:text-rose-600"
                                                            disabled={gesperrt || busy === z.payment_id}
                                                            grund={sperre([gesperrt, grund], [busy === z.payment_id, "Wird ausgeführt"])}
                                                            hilfe="aktion.zahlungKeineMiete"
                                                            aria-label="Keine Miete — nicht mehr vorschlagen"
                                                            onClick={() => keineMiete(z.payment_id)}
                                                            data-testid={`konto-keine-miete-${z.payment_id}`}>
                                                        <X size={12} />
                                                    </Button>
                                                </span>
                                            </div>
                                            <div className="text-[10px] text-gray-400 mt-0.5 pl-[76px] truncate">
                                                {z.grund}
                                                {Math.abs(z.betrag - z.rest) > 0.005 && (
                                                    <> · {euro(z.betrag)} gebucht, Rest {euro(z.rest)}</>
                                                )}
                                            </div>
                                            {/* Verworfene Paare (Auftrag 9, R7b): wann, auf welche
                                                Position, von wem -- und zurücknehmbar. Solange die
                                                Ablehnung steht, schlägt der Vorschlagslauf diese
                                                Zahlung dieser Position nicht wieder vor. */}
                                            {(z.abgelehnt_fuer || []).map((ab) => (
                                                <div key={ab.charge_id}
                                                     className="text-[10px] text-rose-700 mt-0.5 pl-[76px] flex items-center gap-2"
                                                     data-testid={`konto-abgelehnt-${z.payment_id}-${ab.charge_id}`}>
                                                    <span>
                                                        Verworfen für {ab.period || "?"}
                                                        {ab.betrag != null && <> ({euro(ab.betrag)})</>}
                                                        {ab.am && <> am {germanDate(ab.am)}</>}
                                                        {ab.von && <> von {ab.von}</>}
                                                    </span>
                                                    <Button size="sm" variant="ghost" className="h-5 px-1.5 text-[10px]"
                                                            disabled={gesperrt || busy === `ab-${z.payment_id}-${ab.charge_id}`}
                                                            grund={sperre([gesperrt, grund],
                                                                [busy === `ab-${z.payment_id}-${ab.charge_id}`, "Wird zurückgenommen"])}
                                                            onClick={() => mitLaden(`ab-${z.payment_id}-${ab.charge_id}`,
                                                                () => ablehnungZuruecknehmen(z.payment_id, ab.charge_id),
                                                                "Ablehnung zurückgenommen")}
                                                            data-testid={`konto-abgelehnt-zurueck-${z.payment_id}-${ab.charge_id}`}>
                                                        Zurücknehmen
                                                    </Button>
                                                </div>
                                            ))}

                                            {zuweisen?.payment_id === z.payment_id && (
                                                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-md p-2
                                                                flex items-end gap-2 flex-wrap">
                                                    <div className="flex-1 min-w-[200px]">
                                                        <Label className="text-[10px] text-gray-500">Position<Hilfe id="soll-position" /></Label>
                                                        <Select value={ziel} onValueChange={setZiel}>
                                                            <SelectTrigger className="h-8" data-testid="konto-ziel">
                                                                <SelectValue placeholder="— Forderung wählen —" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {daten.positionen
                                                                    .filter((p) => Math.abs(p.offen) > 0.005)
                                                                    .map((p) => (
                                                                        <SelectItem key={p.id} value={p.id}>
                                                                            {p.period} · {p.art === "miete" ? "Miete" : "Abrechnung"}
                                                                            {" · offen "}{euro(p.rest_offen ?? p.offen)}
                                                                        </SelectItem>
                                                                    ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                    <div className="w-28">
                                                        <Label className="text-[10px] text-gray-500">Betrag<Hilfe id="zuordnung" /></Label>
                                                        <Input className="h-8" value={betrag} inputMode="decimal"
                                                               onChange={(e) => setBetrag(e.target.value)}
                                                               data-testid="konto-betrag" />
                                                    </div>
                                                    <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800"
                                                            disabled={busy === "zuweisen"} grund="Wird zugewiesen"
                                                            onClick={zuweisungSpeichern}
                                                            data-testid="konto-zuweisen-ok">
                                                        Zuweisen
                                                    </Button>
                                                    <Button size="sm" variant="ghost" className="h-8"
                                                            onClick={() => setZuweisen(null)}>
                                                        Abbrechen
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* ------------------------------------ Abrechnung hier */}
                        {!neueAbrechnung ? (
                            <Button variant="outline" size="sm" className="h-8"
                                    disabled={gesperrt} grund={grund}
                                    onClick={() => setNeueAbrechnung({
                                        richtung: "nachzahlung", betrag: "",
                                        abrechnungsjahr: String(new Date().getFullYear() - 1),
                                        period: "", due_date: "", notes: "",
                                    })}
                                    data-testid="konto-abrechnung-neu">
                                <Scale size={13} className="mr-1.5" /> Nebenkostenabrechnung erfassen
                            </Button>
                        ) : (
                            <div className="border border-gray-200 rounded-md p-3 space-y-2"
                                 data-testid="konto-abrechnung-form">
                                <div className="text-sm font-medium text-slate-900">
                                    Nebenkostenabrechnung für {tenantName || daten.mieter?.full_name}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                                    <div className="col-span-2">
                                        <Label className="text-[10px] text-gray-500">Richtung<Hilfe id="abrechnung-richtung" /></Label>
                                        <Select value={neueAbrechnung.richtung}
                                                onValueChange={(v) => setNeueAbrechnung((a) => ({ ...a, richtung: v }))}>
                                            <SelectTrigger className="h-8" data-testid="konto-abr-richtung">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="nachzahlung">Nachzahlung — der Mieter zahlt</SelectItem>
                                                <SelectItem value="guthaben">Guthaben — wir erstatten</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-[10px] text-gray-500">Betrag</Label>
                                        <Input className="h-8" placeholder="317,54" inputMode="decimal"
                                               value={neueAbrechnung.betrag}
                                               onChange={(e) => setNeueAbrechnung((a) => ({ ...a, betrag: e.target.value }))}
                                               data-testid="konto-abr-betrag" />
                                    </div>
                                    <div>
                                        <Label className="text-[10px] text-gray-500">Abrechnungsjahr<Hilfe id="faelligkeitsmonat" /></Label>
                                        <Input className="h-8" placeholder="2024"
                                               value={neueAbrechnung.abrechnungsjahr}
                                               onChange={(e) => setNeueAbrechnung((a) => ({ ...a, abrechnungsjahr: e.target.value }))}
                                               data-testid="konto-abr-jahr" />
                                    </div>
                                    <div>
                                        <Label className="text-[10px] text-gray-500">Fällig im Monat<Hilfe id="faelligkeitsmonat" /></Label>
                                        <Input className="h-8" type="month" value={neueAbrechnung.period}
                                               onChange={(e) => setNeueAbrechnung((a) => ({ ...a, period: e.target.value }))}
                                               data-testid="konto-abr-period" />
                                    </div>
                                    <div>
                                        <Label className="text-[10px] text-gray-500">Fällig am<Hilfe id="faellig-am" /></Label>
                                        <Input className="h-8" type="date" value={neueAbrechnung.due_date}
                                               onChange={(e) => setNeueAbrechnung((a) => ({ ...a, due_date: e.target.value }))}
                                               data-testid="konto-abr-faellig" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="text-[10px] text-gray-500">Notiz</Label>
                                        <Input className="h-8" value={neueAbrechnung.notes}
                                               placeholder="woher der Betrag stammt"
                                               onChange={(e) => setNeueAbrechnung((a) => ({ ...a, notes: e.target.value }))} />
                                    </div>
                                </div>
                                <div className="flex justify-end gap-2">
                                    <Button size="sm" variant="ghost" className="h-8"
                                            onClick={() => setNeueAbrechnung(null)}>Abbrechen</Button>
                                    <Button size="sm" className="h-8 bg-slate-900 hover:bg-slate-800"
                                            disabled={busy === "abrechnung"} grund="Wird angelegt" onClick={abrechnungSpeichern}
                                            data-testid="konto-abr-anlegen">
                                        <Plus size={13} className="mr-1" /> Anlegen
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default MieterKontoDialog;
